import { router } from '@inertiajs/react';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { normalizeCredentialSlug } from '@/lib/credential-slug';
import type { PersonForEntrance, SharedProps } from '@/types';

interface AssignCredentialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  person: PersonForEntrance | null;
  eventSlug: string;
  /** entrance mode also offers letting the person in without a credential */
  mode: 'assign' | 'entrance';
  onAssigned?: (person: PersonForEntrance) => void;
  onSkip?: (person: PersonForEntrance) => void;
  isEntering?: boolean;
}

export function AssignCredentialDialog({
  open,
  onOpenChange,
  person,
  eventSlug,
  mode,
  onAssigned,
  onSkip,
  isEntering = false,
}: AssignCredentialDialogProps) {
  const [slug, setSlug] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (open) {
      setSlug(person?.credentialSlug ?? '');
      setError(null);
      setNeedsConfirm(false);
    }
  }, [open, person?.credentialSlug]);

  if (!person) return null;

  const submit = (force: boolean) => {
    const normalized = normalizeCredentialSlug(slug);
    if (!normalized) {
      setError('Enter the slug printed on the card, or paste its QR url');
      return;
    }

    setError(null);
    setIsPending(true);
    router.post(
      `/admin/${eventSlug}/entrance/credential`,
      {
        personId: person.id,
        personType: person.personType,
        slug: normalized,
        force,
      },
      {
        preserveScroll: true,
        onSuccess: (page) => {
          const { flash } = page.props as unknown as SharedProps;
          toast({ type: 'success', description: flash.notice ?? '' });
          onOpenChange(false);
          onAssigned?.(person);
        },
        onError: (errors) => {
          const failure = errors as unknown as Record<
            string,
            string | undefined
          >;
          setError(failure.credential ?? 'Failed to assign credential.');
          setNeedsConfirm(Boolean(failure.conflictWith));
        },
        onFinish: () => setIsPending(false),
      },
    );
  };

  const busy = isPending || isEntering;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>
            {person.credentialSlug ? 'Change credential' : 'Assign credential'}
          </DialogTitle>
          <DialogDescription>
            {person.credentialSlug ? (
              <>
                <span className="font-semibold">{person.fullName}</span> is
                currently on card{' '}
                <code className="rounded bg-muted px-1 py-0.5 text-xs">
                  {person.credentialSlug}
                </code>
                .
              </>
            ) : (
              <>
                <span className="font-semibold">{person.fullName}</span> has no
                printed credential yet.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="credential-slug">Card slug or QR url</Label>
          <Input
            id="credential-slug"
            autoFocus
            placeholder="hacker-7"
            value={slug}
            disabled={busy}
            onChange={(event) => {
              setSlug(event.target.value);
              setNeedsConfirm(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit(needsConfirm);
              }
            }}
          />
          <p className="text-muted-foreground text-xs">
            Read it off the card, or paste the scanned url — only the last path
            segment is used.
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
          {mode === 'entrance' && (
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => {
                onOpenChange(false);
                onSkip?.(person);
              }}
            >
              Enter without credential
            </Button>
          )}
          <Button
            type="button"
            disabled={busy}
            onClick={() => submit(needsConfirm)}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {needsConfirm
              ? 'Reassign anyway'
              : mode === 'entrance'
                ? 'Assign & enter'
                : 'Assign'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
